import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api, { Delivery, DeliveryLine, ProductSuggestion, DeliveryConflictWarning, DeliveryApplyResult } from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import ProductAutocomplete from '../components/ProductAutocomplete';

interface Category {
  id: string;
  name: string;
  icon?: string | null;
}

export default function DeliveryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [delivery, setDelivery] = useState<Delivery | null>(null);
  const [lines, setLines] = useState<DeliveryLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  // États pour l'édition
  const [isEditingSupplier, setIsEditingSupplier] = useState(false);
  const [supplierName, setSupplierName] = useState('');
  const [editingLine, setEditingLine] = useState<DeliveryLine | null>(null);
  const [editedLineData, setEditedLineData] = useState<Partial<DeliveryLine>>({});

  // États pour l'édition en masse
  const [selectedLines, setSelectedLines] = useState<Set<string>>(new Set());
  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [bulkEditData, setBulkEditData] = useState<{
    tax_rate?: number;
    category_hint?: string;
    origin_country?: string;
    default_expiry_date?: string;
  }>({});

  // Catégories existantes
  const [categories, setCategories] = useState<Category[]>([]);

  // États pour les warnings de conflits
  const [conflictWarnings, setConflictWarnings] = useState<DeliveryConflictWarning[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);

  useEffect(() => {
    if (id && tenant?.id) {
      loadDeliveryDetails();
      loadCategories();
    }
  }, [id, tenant?.id]);

  const loadCategories = async () => {
    try {
      const data = await api.getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadDeliveryDetails = async () => {
    if (!id || !tenant?.id) return;

    try {
      const [deliveryData, linesData] = await Promise.all([
        api.getDelivery(tenant.id, id),
        api.getDeliveryLines(tenant.id, id),
      ]);
      setDelivery(deliveryData);
      setLines(linesData);
      setSupplierName(deliveryData.supplier || '');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load delivery details');
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder le fournisseur modifié
  const handleSaveSupplier = async () => {
    if (!id || !tenant?.id || !supplierName.trim()) return;

    try {
      await api.updateDelivery(tenant.id, id, { supplier: supplierName });
      setDelivery(prev => prev ? { ...prev, supplier: supplierName } : null);
      setIsEditingSupplier(false);
      alert('Fournisseur mis à jour avec succès');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Échec de la mise à jour');
    }
  };

  // Ouvrir le modal d'édition de ligne
  const handleEditLine = (line: DeliveryLine) => {
    setEditingLine(line);
    setEditedLineData({
      normalized_name: line.normalized_name || line.raw_name,
      product_code: line.product_code,
      barcode: line.barcode,
      quantity: line.quantity,
      unit: line.unit,
      unit_price_ttc_cents: line.unit_price_ttc_cents,
      tax_rate: line.tax_rate,
      origin_country: line.origin_country,
      organic_certifier_name: line.organic_certifier_name,
      default_expiry_date: line.default_expiry_date,
      category_hint: line.category_hint,
      supplier_lot_number: line.supplier_lot_number,
      producer_name: line.producer_name,
    });
  };

  // Sauvegarder la ligne modifiée
  const handleSaveLine = async () => {
    if (!editingLine || !tenant?.id) return;

    try {
      await api.updateDeliveryLine(tenant.id, editingLine.id, editedLineData);
      // Reload lines
      const linesData = await api.getDeliveryLines(tenant.id, id!);
      setLines(linesData);
      setEditingLine(null);
      setEditedLineData({});
      alert('Ligne mise à jour avec succès');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Échec de la mise à jour');
    }
  };

  // Gérer la sélection de lignes
  const toggleLineSelection = (lineId: string) => {
    setSelectedLines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lineId)) {
        newSet.delete(lineId);
      } else {
        newSet.add(lineId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLines.size === lines.length) {
      setSelectedLines(new Set());
    } else {
      setSelectedLines(new Set(lines.map(l => l.id)));
    }
  };

  // Appliquer l'édition en masse
  const handleBulkEdit = async () => {
    if (!tenant?.id || selectedLines.size === 0) return;

    const updates: Partial<DeliveryLine> = {};
    if (bulkEditData.tax_rate !== undefined) {
      updates.tax_rate = bulkEditData.tax_rate;
    }
    if (bulkEditData.category_hint !== undefined && bulkEditData.category_hint !== '') {
      updates.category_hint = bulkEditData.category_hint;
    }
    if (bulkEditData.origin_country !== undefined && bulkEditData.origin_country !== '') {
      updates.origin_country = bulkEditData.origin_country;
    }
    if (bulkEditData.default_expiry_date !== undefined && bulkEditData.default_expiry_date !== '') {
      updates.default_expiry_date = bulkEditData.default_expiry_date;
    }

    if (Object.keys(updates).length === 0) {
      alert('Veuillez renseigner au moins un champ à modifier');
      return;
    }

    try {
      // Appliquer les modifications à toutes les lignes sélectionnées
      await Promise.all(
        Array.from(selectedLines).map(lineId =>
          api.updateDeliveryLine(tenant.id, lineId, updates)
        )
      );

      // Recharger les lignes
      const linesData = await api.getDeliveryLines(tenant.id, id!);
      setLines(linesData);
      setSelectedLines(new Set());
      setIsBulkEditing(false);
      setBulkEditData({});
      alert(`✅ ${selectedLines.size} ligne(s) mise(s) à jour avec succès`);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Échec de la mise à jour en masse');
    }
  };

  const handleApproveLine = async (lineId: string) => {
    if (!tenant?.id) return;

    try {
      await api.approveLine(tenant.id, lineId);
      // Reload lines
      const linesData = await api.getDeliveryLines(tenant.id, id!);
      setLines(linesData);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to approve line');
    }
  };

  const handleRejectLine = async (lineId: string) => {
    if (!tenant?.id) return;

    try {
      await api.rejectLine(tenant.id, lineId);
      // Reload lines
      const linesData = await api.getDeliveryLines(tenant.id, id!);
      setLines(linesData);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to reject line');
    }
  };

  const handleApplyDelivery = async (force: boolean = false) => {
    if (!id || !tenant?.id) return;

    if (!force && !window.confirm('Êtes-vous sûr de vouloir appliquer cette livraison au catalogue ?')) {
      return;
    }

    setApplying(true);
    try {
      const result: DeliveryApplyResult = await api.applyDelivery(tenant.id, id, force);

      // Vérifier si on a des warnings (sans force)
      if (result.status === 'warnings' && result.warnings && result.warnings.length > 0) {
        setConflictWarnings(result.warnings);
        setShowConflictModal(true);
        return;
      }

      // Succès
      alert(`✅ Livraison appliquée avec succès !\n\n📊 Résumé:\n• ${result.products_created || 0} produits créés\n• ${result.products_updated || 0} produits mis à jour\n• ${result.lots_created || 0} lots créés`);
      navigate('/deliveries');
    } catch (err: any) {
      console.error('[DeliveryDetail] Erreur application livraison:', err);

      // Extraire le vrai message d'erreur
      let errorMessage = 'Échec de l\'application de la livraison';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Afficher une alerte plus détaillée
      alert(`❌ Erreur d'application\n\n${errorMessage}\n\nStatut HTTP: ${err.response?.status || 'N/A'}`);
    } finally {
      setApplying(false);
    }
  };

  const handleForceApply = async () => {
    setShowConflictModal(false);
    await handleApplyDelivery(true);
  };

  const handleDeleteDelivery = async () => {
    if (!id || !tenant?.id || !window.confirm('⚠️ Êtes-vous sûr de vouloir supprimer cette livraison ?\n\nCette action est irréversible.')) {
      return;
    }

    try {
      await api.deleteDelivery(tenant.id, id);
      alert('✅ Livraison supprimée avec succès');
      navigate('/deliveries');
    } catch (err: any) {
      console.error('[DeliveryDetail] Erreur suppression livraison:', err);
      alert(err.response?.data?.message || 'Échec de la suppression de la livraison');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading delivery...</p>
        </div>
      </div>
    );
  }

  if (error || !delivery) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-800">{error || 'Delivery not found'}</p>
        <Link to="/deliveries" className="mt-4 inline-block text-emerald-600 hover:text-emerald-900">
          ← Retour aux livraisons
        </Link>
      </div>
    );
  }

  const approvedCount = lines.filter(l => l.status === 'approved').length;
  const pendingCount = lines.filter(l => l.status === 'pending').length;
  const rejectedCount = lines.filter(l => l.status === 'rejected').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link to="/deliveries" className="text-sm text-emerald-600 hover:text-emerald-900 mb-2 inline-block">
            ← Retour aux livraisons
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            Livraison #{delivery.document_number || delivery.id.slice(0, 8)}
          </h1>
        </div>
        <div className="flex gap-2">
          {/* Bouton document source (traçabilité AFSCA) */}
          {delivery.document_url && (
            <a
              href={delivery.document_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2"
              title="Voir le document original"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Document
            </a>
          )}
          {delivery.status !== 'applied' && (
            <button
              onClick={handleDeleteDelivery}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
            >
              🗑️ Supprimer
            </button>
          )}
          <button
            onClick={() => handleApplyDelivery()}
            disabled={applying || delivery.status === 'applied'}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
          >
            {applying ? 'Application...' : 'Appliquer au Catalogue'}
          </button>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Informations</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-600">Fournisseur</p>
            {isEditingSupplier ? (
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                  autoFocus
                />
                <button
                  onClick={handleSaveSupplier}
                  className="text-green-600 hover:text-green-800"
                  title="Sauvegarder"
                >
                  ✓
                </button>
                <button
                  onClick={() => {
                    setIsEditingSupplier(false);
                    setSupplierName(delivery.supplier || '');
                  }}
                  className="text-red-600 hover:text-red-800"
                  title="Annuler"
                >
                  ✗
                </button>
              </div>
            ) : (
              <div className="flex gap-2 items-center">
                <p className="text-lg font-semibold">{delivery.supplier}</p>
                <button
                  onClick={() => setIsEditingSupplier(true)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                  title="Modifier le fournisseur"
                >
                  ✏️
                </button>
              </div>
            )}
          </div>
          <div>
            <p className="text-sm text-gray-600">Type</p>
            <p className="text-lg font-semibold">{delivery.document_type}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Méthode</p>
            <p className="text-lg font-semibold">{delivery.parsing_method}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Confiance IA</p>
            {delivery.ai_confidence_score ? (
              <p className="text-lg font-semibold">{delivery.ai_confidence_score}%</p>
            ) : (
              <p className="text-lg text-gray-400">-</p>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <p className="text-sm font-medium text-blue-600">En attente</p>
          <p className="text-2xl font-bold text-blue-900">{pendingCount}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <p className="text-sm font-medium text-green-600">Approuvées</p>
          <p className="text-2xl font-bold text-green-900">{approvedCount}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <p className="text-sm font-medium text-red-600">Rejetées</p>
          <p className="text-2xl font-bold text-red-900">{rejectedCount}</p>
        </div>
      </div>

      {/* Lines Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Lignes de Livraison ({lines.length})</h2>
            <div className="flex gap-2">
              {selectedLines.size > 0 && (
                <>
                  <span className="text-sm text-gray-600 flex items-center">
                    {selectedLines.size} ligne(s) sélectionnée(s)
                  </span>
                  <button
                    onClick={() => setIsBulkEditing(true)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                  >
                    ✏️ Éditer en masse
                  </button>
                  <button
                    onClick={() => setSelectedLines(new Set())}
                    className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                  >
                    Désélectionner
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left sticky left-0 bg-gray-50 z-10">
                  <input
                    type="checkbox"
                    checked={selectedLines.size === lines.length && lines.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-10 bg-gray-50 z-10">
                  Actions
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produit
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code-barres
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Catégorie
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantité
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix achat TTC
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  TVA
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Origine
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Certif. Bio
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  DLC
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {lines.map((line) => (
                <tr key={line.id} className="hover:bg-gray-50">
                  <td className="px-3 py-4 sticky left-0 bg-white z-10">
                    <input
                      type="checkbox"
                      checked={selectedLines.has(line.id)}
                      onChange={() => toggleLineSelection(line.id)}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap sticky left-10 bg-white z-10 border-r border-gray-100">
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEditLine(line)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Éditer"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      {line.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApproveLine(line.id)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                            title="Approuver"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleRejectLine(line.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Rejeter"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      line.status === 'approved' ? 'bg-green-100 text-green-800' :
                      line.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      line.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {line.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-gray-900 max-w-[200px]">
                      {line.normalized_name || line.raw_name}
                    </div>
                    {line.normalized_name && line.raw_name !== line.normalized_name && (
                      <div className="text-xs text-gray-500 truncate max-w-[200px]">
                        Original: {line.raw_name}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {line.product_code || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {line.barcode || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {line.category_hint || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {line.quantity ? `${line.quantity} ${line.unit || ''}` : '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {line.unit_price_ttc_cents ? `${(line.unit_price_ttc_cents / 100).toFixed(2)}€` : '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {line.tax_rate ? `${line.tax_rate}%` : '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {line.origin_country || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {line.organic_certifier_name || '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {line.default_expiry_date ? new Date(line.default_expiry_date).toLocaleDateString('fr-FR') : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal d'édition en masse */}
      {isBulkEditing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                Éditer {selectedLines.size} ligne(s) en masse
              </h2>
              <p className="text-sm text-gray-600 mt-2">
                Les champs renseignés ci-dessous seront appliqués à toutes les lignes sélectionnées
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  💡 <strong>Astuce :</strong> Laissez un champ vide si vous ne souhaitez pas le modifier
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    TVA (%)
                  </label>
                  <select
                    value={bulkEditData.tax_rate ?? ''}
                    onChange={(e) => setBulkEditData({
                      ...bulkEditData,
                      tax_rate: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">-- Ne pas modifier --</option>
                    <option value="0">0% (Vidange/Exonéré)</option>
                    <option value="6">6% (Taux alimentaire)</option>
                    <option value="21">21% (Taux normal)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Catégorie
                  </label>
                  <select
                    value={bulkEditData.category_hint ?? ''}
                    onChange={(e) => setBulkEditData({
                      ...bulkEditData,
                      category_hint: e.target.value || undefined
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">-- Ne pas modifier --</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pays d'origine
                  </label>
                  <input
                    type="text"
                    value={bulkEditData.origin_country ?? ''}
                    onChange={(e) => setBulkEditData({
                      ...bulkEditData,
                      origin_country: e.target.value || undefined
                    })}
                    placeholder="Ex: France, Belgique, Espagne..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DLC / DDM
                  </label>
                  <input
                    type="date"
                    value={bulkEditData.default_expiry_date ?? ''}
                    onChange={(e) => setBulkEditData({
                      ...bulkEditData,
                      default_expiry_date: e.target.value || undefined
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-amber-800">
                  <strong>Fournisseur :</strong> Pour modifier le fournisseur, utilisez le bouton d'édition dans la section "Informations" en haut de la page (le fournisseur s'applique à toute la livraison)
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsBulkEditing(false);
                  setBulkEditData({});
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleBulkEdit}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
              >
                Appliquer à {selectedLines.size} ligne(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition de ligne */}
      {editingLine && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                Éditer le produit
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du produit *
                  </label>
                  <ProductAutocomplete
                    value={editedLineData.normalized_name || ''}
                    onChange={(value) => setEditedLineData({...editedLineData, normalized_name: value})}
                    onProductSelect={(product: ProductSuggestion) => {
                      // Remplir automatiquement les champs si un produit existant est sélectionné
                      setEditedLineData({
                        ...editedLineData,
                        normalized_name: product.name,
                        product_code: product.code || editedLineData.product_code,
                        barcode: product.barcode || editedLineData.barcode,
                        category_hint: product.category_name || editedLineData.category_hint,
                        origin_country: product.origin_country || editedLineData.origin_country,
                      });
                    }}
                    placeholder="Tapez pour rechercher un produit existant..."
                  />
                  {editingLine?.raw_name && editingLine.raw_name !== editedLineData.normalized_name && (
                    <p className="text-xs text-gray-500 mt-1">
                      Nom original : {editingLine.raw_name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code produit
                  </label>
                  <input
                    type="text"
                    value={editedLineData.product_code || ''}
                    onChange={(e) => setEditedLineData({...editedLineData, product_code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code-barres
                  </label>
                  <input
                    type="text"
                    value={editedLineData.barcode || ''}
                    onChange={(e) => setEditedLineData({...editedLineData, barcode: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Catégorie
                  </label>
                  <select
                    value={editedLineData.category_hint || ''}
                    onChange={(e) => setEditedLineData({...editedLineData, category_hint: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">-- Sélectionner --</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantité *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editedLineData.quantity || ''}
                    onChange={(e) => setEditedLineData({...editedLineData, quantity: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unité
                  </label>
                  <input
                    type="text"
                    value={editedLineData.unit || ''}
                    onChange={(e) => setEditedLineData({...editedLineData, unit: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="kg, pièce, L..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prix achat TTC (€) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editedLineData.unit_price_ttc_cents ? (editedLineData.unit_price_ttc_cents / 100).toFixed(2) : ''}
                    onChange={(e) => setEditedLineData({...editedLineData, unit_price_ttc_cents: Math.round(parseFloat(e.target.value) * 100)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    TVA (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={editedLineData.tax_rate || ''}
                    onChange={(e) => setEditedLineData({...editedLineData, tax_rate: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pays d'origine
                  </label>
                  <input
                    type="text"
                    value={editedLineData.origin_country || ''}
                    onChange={(e) => setEditedLineData({...editedLineData, origin_country: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="France, Espagne..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Certification Bio
                  </label>
                  <input
                    type="text"
                    value={editedLineData.organic_certifier_name || ''}
                    onChange={(e) => setEditedLineData({...editedLineData, organic_certifier_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="AB, Demeter..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DLC / DDM
                  </label>
                  <input
                    type="date"
                    value={editedLineData.default_expiry_date ? new Date(editedLineData.default_expiry_date).toISOString().split('T')[0] : ''}
                    onChange={(e) => setEditedLineData({...editedLineData, default_expiry_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    N° Lot Fournisseur
                  </label>
                  <input
                    type="text"
                    value={(editedLineData as any).supplier_lot_number || ''}
                    onChange={(e) => setEditedLineData({...editedLineData, supplier_lot_number: e.target.value} as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Numéro de lot du fournisseur"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du Producteur
                  </label>
                  <input
                    type="text"
                    value={(editedLineData as any).producer_name || ''}
                    onChange={(e) => setEditedLineData({...editedLineData, producer_name: e.target.value} as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Nom de l'exploitation/producteur"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setEditingLine(null);
                  setEditedLineData({});
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveLine}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal des conflits/warnings */}
      {showConflictModal && conflictWarnings.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 bg-yellow-50">
              <div className="flex items-center gap-3">
                <span className="text-3xl">⚠️</span>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Produits similaires détectés
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {conflictWarnings.length} produit(s) seront créés alors que des produits similaires existent déjà.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {conflictWarnings.map((warning) => (
                <div key={warning.line_id} className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">📦</span>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{warning.line_name}</p>
                      <p className="text-sm text-gray-600 mt-1">{warning.message}</p>

                      {warning.similar_products && warning.similar_products.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-gray-500 mb-2">Produits similaires existants :</p>
                          <div className="space-y-2">
                            {warning.similar_products.map((product) => (
                              <div
                                key={product.id}
                                className="flex items-center justify-between bg-white p-2 rounded border border-gray-200"
                              >
                                <div>
                                  <span className="font-medium text-sm">{product.name}</span>
                                  {product.code && (
                                    <span className="text-xs text-gray-500 ml-2">({product.code})</span>
                                  )}
                                </div>
                                <span
                                  className={`text-xs px-2 py-1 rounded-full ${
                                    product.similarity_score >= 80
                                      ? 'bg-green-100 text-green-700'
                                      : product.similarity_score >= 60
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {product.similarity_score}% similaire
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600 mb-4">
                Vous pouvez modifier les lignes pour lier aux produits existants, ou continuer pour créer de nouveaux produits.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConflictModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Annuler et modifier
                </button>
                <button
                  onClick={handleForceApply}
                  disabled={applying}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium disabled:bg-gray-300"
                >
                  {applying ? 'Application...' : 'Créer quand même les nouveaux produits'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
